import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, fmtDate, fmtMoney, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, StatusBadge } from "@/pages/AppLayout";
import { TICKET_LABEL } from "@/pages/Support";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlansAdmin, PricingAdmin, CouponsAdmin, SubscriptionsAdmin, OperationsAdmin, FinanceAdmin, SecurityAdmin } from "@/pages/AdminCommerce";

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const items = [["Usuarios", s.users, "users"], ["Proyectos", s.projects, "projects"], ["Publicadas", s.published, "published"], ["Versiones", s.versions, "versions"],
    ["Mensajes IA", s.ai_messages, "ai"], ["Pagos", s.payments, "payments"], ["Ingresos", fmtMoney(s.revenue, "usd"), "revenue"], ["Tickets abiertos", s.open_tickets, "tickets"]];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map(([l, v, id]) => <Card key={id}><p className="text-xs uppercase tracking-widest text-slate-500">{l}</p><p className="font-display text-2xl font-bold text-white mt-1" data-testid={`admin-stat-${id}`}>{v}</p></Card>)}
    </div>
  );
}

function LimitDialog({ u, onSave }) {
  const [open, setOpen] = useState(false);
  const [v, setV] = useState(u.monthly_limit || 0);
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); setV(u.monthly_limit || 0); }}>
      <DialogTrigger asChild><button className="h-8 px-3 rounded-lg border border-[#222B40] text-slate-300 text-xs hover:text-white" data-testid={`admin-limit-user-${u.id}`}>Límite{u.monthly_limit ? `: ${u.monthly_limit}` : ""}</button></DialogTrigger>
      <DialogContent className="bg-[#111622] border-[#222B40]" data-testid="admin-limit-dialog">
        <DialogHeader><DialogTitle className="text-white">Límite mensual de {u.name}</DialogTitle><DialogDescription>Créditos máximos que puede consumir al mes (0 = sin límite).</DialogDescription></DialogHeader>
        <Input type="number" min={0} value={v} onChange={(e) => setV(e.target.value)} className="bg-[#0A0D14]" data-testid="admin-limit-input" />
        <DialogFooter><button onClick={() => { onSave(Math.max(0, parseInt(v, 10) || 0)); setOpen(false); }} className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm" data-testid="admin-limit-save-btn">Guardar límite</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserRow({ u, me, onChanged }) {
  const [amount, setAmount] = useState("");
  const call = async (fn, msg) => { try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(formatError(e)); } };
  return (
    <tr className="border-b border-[#222B40]/60 last:border-0" data-testid={`admin-user-row-${u.id}`}>
      <td className="p-3"><p className="text-white">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></td>
      <td className="p-3 text-slate-300">{u.role === "admin" ? "Admin" : "Usuario"}{u.disabled && <span className="ml-2 text-xs text-red-300">(desactivado)</span>}</td>
      <td className="p-3 text-slate-300 capitalize">{u.plan === "sin_plan" ? "—" : u.plan}</td>
      <td className="p-3 text-slate-300">{u.project_count}</td>
      <td className="p-3 font-mono text-white" data-testid={`admin-user-credits-${u.id}`}>{u.credits}</td>
      <td className="p-3">
        <div className="flex gap-2 items-center">
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="±" className="w-20 h-8 bg-[#0A0D14]" data-testid={`admin-credit-input-${u.id}`} />
          <button disabled={!amount} onClick={() => call(() => api.post(`/admin/users/${u.id}/credits`, { amount: parseInt(amount, 10), reason: "Ajuste manual" }), "Créditos actualizados").then(() => setAmount(""))}
            className="h-8 px-3 rounded-lg bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white text-xs" data-testid={`admin-credit-apply-${u.id}`}>Aplicar</button>
          <LimitDialog u={u} onSave={(v) => call(() => api.patch(`/admin/users/${u.id}/limit`, { monthly_limit: v }), "Límite actualizado")} />
          {u.id !== me && (
            <button onClick={() => call(() => api.patch(`/admin/users/${u.id}`, { disabled: !u.disabled }), u.disabled ? "Cuenta reactivada" : "Cuenta suspendida")}
              className="h-8 px-3 rounded-lg border border-[#222B40] text-slate-300 text-xs hover:text-white" data-testid={`admin-toggle-user-${u.id}`}>{u.disabled ? "Reactivar" : "Suspender"}</button>
          )}
        </div>
      </td>
    </tr>
  );
}

function Users() {
  const { user, refreshUser } = useAuth();
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const load = useCallback(() => { api.get("/admin/users", { params: { q } }).then((r) => setUsers(r.data)); refreshUser(); }, [q, refreshUser]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  return (
    <Card className="p-0 overflow-x-auto">
      <div className="p-4 border-b border-[#222B40]"><Input placeholder="Buscar por nombre o correo…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm bg-[#0A0D14]" data-testid="admin-user-search" /></div>
      <table className="w-full text-sm" data-testid="admin-users-table">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#222B40]"><th className="p-3">Usuario</th><th className="p-3">Rol</th><th className="p-3">Plan</th><th className="p-3">Proyectos</th><th className="p-3">Créditos</th><th className="p-3">Acciones</th></tr></thead>
        <tbody>{users.map((u) => <UserRow key={u.id} u={u} me={user.id} onChanged={load} />)}</tbody>
      </table>
    </Card>
  );
}

function Integrations() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/integrations").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const active = d.ai_providers.find((p) => p.active);
  const setModel = async (val) => {
    const [provider, model] = val.split("|");
    try { await api.put("/admin/ai-config", { provider, model }); toast.success(`Modelo activo: ${model}`); load(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div><h3 className="text-white font-medium">Modelo de IA del Agente Constructor</h3><p className="text-sm text-slate-400 mt-1">Arquitectura multi-proveedor: cambia el modelo usado para todas las generaciones.</p></div>
          <Select value={`${active.id}|${active.active_model}`} onValueChange={setModel}>
            <SelectTrigger className="w-72 bg-[#0A0D14]" data-testid="admin-ai-model-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#161C2E] border-[#222B40]">
              {d.ai_providers.flatMap((p) => p.models.map((m) => <SelectItem key={m} value={`${p.id}|${m}`} disabled={!p.configured} data-testid={`admin-ai-option-${m}`}>{p.label} · {m}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-6 grid md:grid-cols-3 gap-3">
          {d.ai_providers.map((p) => (
            <div key={p.id} className="rounded-xl border border-[#222B40] p-4" data-testid={`admin-provider-${p.id}`}>
              <div className="flex justify-between items-center"><span className="text-white text-sm">{p.label}</span><StatusBadge ok={p.configured}>{p.configured ? "Configurado" : "No configurado"}</StatusBadge></div>
              <p className="text-xs text-slate-500 mt-2">{p.active ? `Activo · ${p.active_model}` : "Disponible"}</p>
            </div>
          ))}
        </div>
      </Card>
      <div className="grid md:grid-cols-2 gap-4">
        {d.integrations.map((i) => (
          <Card key={i.id} data-testid={`admin-integration-${i.id}`}>
            <div className="flex justify-between items-center"><span className="text-white">{i.label}</span><StatusBadge ok={i.status === "configurado"}>{i.status === "configurado" ? "Configurado" : "No configurado"}</StatusBadge></div>
            <p className="text-sm text-slate-400 mt-2">{i.detail}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Payments() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/payments").then((r) => setRows(r.data)); }, []);
  return (
    <Card className="p-0 overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-payments-table">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#222B40]"><th className="p-3">Fecha</th><th className="p-3">Producto</th><th className="p-3">Estado</th><th className="p-3 text-right">Importe</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500">Sin pagos.</td></tr>}
          {rows.map((r) => <tr key={r.session_id} className="border-b border-[#222B40]/60"><td className="p-3 text-slate-400">{fmtDate(r.created_at)}</td><td className="p-3 text-xs text-slate-300">{r.item_name} · {r.user_email || ""} · {r.provider}</td><td className="p-3 text-slate-300">{r.payment_status}</td><td className="p-3 text-right font-mono">{fmtMoney(r.amount, r.currency)}</td></tr>)}
        </tbody>
      </table>
    </Card>
  );
}

function Tickets() {
  const [rows, setRows] = useState([]);
  const [replies, setReplies] = useState({});
  const load = useCallback(() => api.get("/admin/tickets").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const update = async (t, status) => {
    try { await api.patch(`/admin/tickets/${t.id}`, { status, reply: replies[t.id] ?? t.reply }); toast.success("Ticket actualizado"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-3">
      {rows.length === 0 && <Card className="text-slate-500 text-sm">Sin tickets.</Card>}
      {rows.map((t) => (
        <Card key={t.id} data-testid={`admin-ticket-${t.id}`}>
          <div className="flex justify-between gap-3"><div><h3 className="text-white">{t.subject}</h3><p className="text-xs text-slate-500">{t.user_email} · {fmtDate(t.created_at)}</p></div><StatusBadge ok={t.status === "resuelto"}>{TICKET_LABEL[t.status]}</StatusBadge></div>
          <p className="text-sm text-slate-300 mt-3 whitespace-pre-wrap">{t.message}</p>
          <Input className="mt-4 bg-[#0A0D14]" placeholder="Respuesta…" value={replies[t.id] ?? t.reply ?? ""} onChange={(e) => setReplies({ ...replies, [t.id]: e.target.value })} data-testid={`admin-ticket-reply-${t.id}`} />
          <div className="flex gap-2 mt-3">
            <button onClick={() => update(t, "en_proceso")} className="px-3 py-1.5 rounded-lg border border-[#222B40] text-xs text-slate-300" data-testid={`admin-ticket-progress-${t.id}`}>En proceso</button>
            <button onClick={() => update(t, "resuelto")} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-xs text-white" data-testid={`admin-ticket-resolve-${t.id}`}>Responder y resolver</button>
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function Admin() {
  return (
    <div data-testid="admin-page">
      <PageHeader eyebrow="Administración" title="Panel de administración" subtitle="Usuarios, créditos, pagos, soporte y estado de integraciones de la plataforma." />
      <Tabs defaultValue="overview">
        <TabsList className="bg-[#111622] border border-[#222B40] mb-6 flex-wrap h-auto">
          {[["overview", "Resumen"], ["users", "Usuarios"], ["plans", "Planes y paquetes"], ["pricing", "Motor de costos"], ["coupons", "Cupones"], ["subs", "Suscripciones"], ["ops", "Consumo IA"], ["finance", "Finanzas"], ["integrations", "IA e integraciones"], ["payments", "Pagos"], ["tickets", "Soporte"], ["security", "Seguridad"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview"><Overview /></TabsContent>
        <TabsContent value="users"><Users /></TabsContent>
        <TabsContent value="plans"><PlansAdmin /></TabsContent>
        <TabsContent value="pricing"><PricingAdmin /></TabsContent>
        <TabsContent value="coupons"><CouponsAdmin /></TabsContent>
        <TabsContent value="subs"><SubscriptionsAdmin /></TabsContent>
        <TabsContent value="ops"><OperationsAdmin /></TabsContent>
        <TabsContent value="finance"><FinanceAdmin /></TabsContent>
        <TabsContent value="security"><SecurityAdmin /></TabsContent>
        <TabsContent value="integrations"><Integrations /></TabsContent>
        <TabsContent value="payments"><Payments /></TabsContent>
        <TabsContent value="tickets"><Tickets /></TabsContent>
      </Tabs>
    </div>
  );
}
